// frontend/app/components/lessonPlans/LessonPlanLauncher.js
//
// Dashboard entry point for Lesson Plans, on both the teacher and student home
// screens.
//
// Deliberately unlike `SummerCampJourneyLauncher`, whose catalog is a literal
// `SUMMER_CAMP_JOURNEYS = [...]` array in the bundle (and which matches lessons
// by English title string). Everything here comes from the API, so seeding a
// new lesson makes it appear with no code change and no rebuild.
//
// The tile grid this used to render is gone: teachers now describe the lesson
// they want and pick from what matches (`LessonFinder`). A grid works at twelve
// lessons and stops working at fifty, and the list it was drawing is the same
// list the finder ranks — so what remains here is the frame around it, not a
// second way in.
//
// `LessonResumeBand` and `pickResumeLesson` ship from here because they are
// lesson-list logic, but the band is deliberately NOT rendered by the launcher:
// the dashboards have their own hero areas, and the lessons index is the one
// screen where a Continue band is not a duplicate.

import React, { useCallback, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Image,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import { getLessonColors } from "./lessonPlanTheme";
import { ActionButton } from "./blocks/LessonBlockShell";
import LessonFinder from "./LessonFinder";
import LessonAuthorFilter from "./LessonAuthorFilter";
import StartLessonCard from "./StartLessonCard";
import { LessonForkProvider } from "./LessonResultRow";
import { filterLessonsByAuthor } from "./lessonAuthorFilterRules";
import { apiMatchLessonPlans } from "../../lessonPlans";

const NARROW_MAX = 720;

const METER_TRACK = 96;

/**
 * The Saras mark, at the size and shape the Saras rail already uses it — an
 * existing asset in an existing treatment, rather than a new one invented for
 * this page. It carries no theme variant because it does not need one: the
 * rail renders this same file in both.
 */
const SARAS_MARK = require("../../../assets/Saras-3D_logo_bird.png");

const DONE_HEX = "#10b981";

/**
 * Interpolates `{placeholders}` into an already-translated sentence, so a
 * translator receives one whole clause instead of fragments that only reassemble
 * in English word order.
 */
const fill = (template, values) =>
  String(template).replace(/\{(\w+)\}/g, (token, key) =>
    Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : token
  );

const breadcrumbOf = (lesson) =>
  [lesson?.chapter_name, lesson?.topic_name].filter(Boolean).join("  ›  ");

/**
 * The lesson to offer as "continue where you left off": the most recently
 * touched one that is not finished. Exported so the lessons index can render the
 * band without re-deriving the rule.
 */
export const pickResumeLesson = (lessons = [], progressByLesson = {}) =>
  (Array.isArray(lessons) ? lessons : [])
    .filter((lesson) => {
      const progress = progressByLesson[lesson.id];
      return Boolean(progress) && !progress.completed;
    })
    .sort((a, b) =>
      String(progressByLesson[b.id]?.updated_at || "").localeCompare(
        String(progressByLesson[a.id]?.updated_at || "")
      )
    )[0] || null;

/**
 * Continue band for the lessons index. Renders nothing when there is nothing to
 * resume, so a first visit and a finished chapter both stay silent.
 */
export const LessonResumeBand = ({
  lessons = [],
  progressByLesson = {},
  isDarkMode = false,
  isNarrow = false,
  onOpenLesson,
  t,
}) => {
  const translate = t || ((value) => value);
  const colors = getLessonColors(isDarkMode);
  const lesson = pickResumeLesson(lessons, progressByLesson);
  if (!lesson) return null;

  const progress = progressByLesson[lesson.id];
  const total = Number(lesson.step_count) || 0;
  const step = Math.min((Number(progress.step_index) || 0) + 1, total || 1);
  const breadcrumb = breadcrumbOf(lesson);
  const stepText = total
    ? fill(translate("Step {step} of {total}"), { step, total })
    : translate("In progress");

  return (
    <View
      testID="lesson-resume-band"
      style={[
        styles.resume,
        isNarrow && styles.resumeStacked,
        {
          backgroundColor: colors.accentGlow,
          borderColor: colors.border,
          borderLeftColor: colors.accentPrimary,
        },
      ]}
    >
      <View style={styles.resumeBody}>
        <Text style={[styles.resumeEyebrow, { color: colors.accentPrimary }]}>
          {translate("Continue")}
        </Text>
        <Text
          style={[styles.resumeTitle, { color: colors.textPrimary }]}
          numberOfLines={2}
        >
          {fill(translate("Lesson {number} · {title}"), {
            number: lesson.lesson_no,
            title: lesson.title,
          })}
        </Text>
        <Text
          style={[styles.resumeMeta, { color: colors.textSecondary }]}
          numberOfLines={1}
        >
          {breadcrumb ? `${stepText}  ·  ${breadcrumb}` : stepText}
        </Text>
      </View>
      <ActionButton
        colors={colors}
        label={translate("Resume lesson")}
        icon="play"
        onPress={() => onOpenLesson && onOpenLesson(lesson)}
      />
    </View>
  );
};

/**
 * Class progress as a labelled meter. `1/3` beside three cards reads as
 * pagination, so the same numbers get their noun; with nothing completed the
 * track is dropped entirely rather than sitting empty and inviting that read
 * again.
 */
const ProgressMeter = ({ completed, total, colors, isDarkMode, t }) => {
  const translate = t || ((value) => value);
  const allDone = completed === total;
  const pct = total ? Math.round((completed / total) * 100) : 0;

  const label = allDone
    ? fill(translate("All {total} completed"), { total })
    : completed > 0
      ? fill(translate("{completed} of {total} completed"), { completed, total })
      : fill(translate("{total} lessons"), { total });

  return (
    <View style={styles.meter}>
      {completed > 0 ? (
        <View
          testID="lesson-meter-track"
          style={[
            styles.meterTrack,
            {
              backgroundColor: isDarkMode ? "rgba(148,163,184,0.18)" : "#e2e8f0",
            },
          ]}
        >
          <View
            style={[
              styles.meterFill,
              {
                width: `${pct}%`,
                backgroundColor: allDone ? DONE_HEX : colors.accentPrimary,
              },
            ]}
          />
        </View>
      ) : null}
      <Text
        style={[
          styles.meterText,
          { color: allDone ? DONE_HEX : colors.textSecondary },
        ]}
      >
        {label}
      </Text>
    </View>
  );
};

const LessonPlanLauncher = ({
  lessons = [],
  // { [lesson_plan_id]: { step_index, completed, ... } }
  progressByLesson = {},
  onEditLesson,
  loading = false,
  error = null,
  isDarkMode = false,
  onOpenLesson,
  title = "Lesson Plans",
  subtitle = "Everything you need to run the class, in order.",
  // Node rendered at the right of the header, beside the meter. The lessons
  // index passes its New lesson button; the dashboards pass nothing.
  headerRight = null,
  // Node rendered between the header and the finder — the lessons index puts
  // its Continue band here, which is why the band ships from this file but is
  // never rendered by the launcher itself.
  banner = null,
  // Turns the finder's AI fallback on: with it, a description that matches no
  // words in the library is handed to Saras. Without it the finder is local
  // ranking only, which is a complete feature — a caller that has no token to
  // spend is not a broken caller.
  fetchWithAuth = null,
  // Narrows that fallback to the board this list came from, so Saras picks from
  // the same library the teacher is looking at.
  board = null,
  // Who is reading the finder: "teacher" | "student". The student dashboard is
  // the same lesson list found the same way, but a student has no class and
  // picks nothing for one, so the box does not ask them to plan for one.
  audience = "teacher",
  // "section" | "page". A dashboard column shares a scroll with everything else
  // on the screen, so the launcher introduces itself there: title, subtitle,
  // progress. The lessons index IS this component, so it drops the introduction
  // and becomes one centred question with a box under it — there is nothing
  // else on that page to be distinguished from.
  layout = "section",
  // Teacher-only, page layout only: the quiet way to a blank lesson, under the
  // box rather than in it. Authoring is the rarer intent on a page whose whole
  // purpose is finding what already exists.
  onCreateLesson = null,
  // Teacher-only, page layout only: `(lesson, warnings) => void`, called with
  // the DRAFT created from an uploaded lesson JSON file. Needs fetchWithAuth.
  onLessonImported = null,
  // Teacher-only, page layout only: "Make my copy" on a lesson the viewer
  // cannot edit. `(lesson) => void`; reaches the rows via LessonForkProvider
  // below — see LessonResultRow.js for why that indirection exists — rather
  // than as a prop LessonFinder or LessonBrowseTree would have to forward.
  onForkLesson = null,
  // The id of the lesson currently being copied, so its row alone shows
  // "Copying…" while every other "Make my copy" stays pressable.
  forkingLessonId = null,
  style,
  t = (s) => s,
}) => {
  const colors = getLessonColors(isDarkMode);
  const { width: windowWidth } = useWindowDimensions();
  const [measuredWidth, setMeasuredWidth] = useState(0);
  const width = measuredWidth || windowWidth;
  const isNarrow = width < NARROW_MAX;
  const isPage = layout === "page";

  const completedCount = useMemo(
    () => lessons.filter((lesson) => progressByLesson[lesson.id]?.completed).length,
    [lessons, progressByLesson]
  );

  // Who wrote it — page layout only, see LessonAuthorFilter.js. Held here
  // rather than in the filter control itself, even though the control owns
  // its own persisted value: this is what the filtered list below is derived
  // from, and derivation belongs where the thing it feeds is rendered.
  const [authorFilter, setAuthorFilter] = useState(null);
  const filteredLessons = useMemo(
    () => (isPage && authorFilter ? filterLessonsByAuthor(lessons, authorFilter) : lessons),
    [isPage, authorFilter, lessons]
  );
  // A filter narrowed a non-empty board to nothing — as opposed to the board
  // itself having nothing, which the early return below already handles.
  // Only possible in page layout, since only page layout can select a filter
  // at all.
  const filterEmptyText = useMemo(() => {
    if (!isPage || !authorFilter || lessons.length === 0 || filteredLessons.length > 0) {
      return null;
    }
    if (authorFilter.mode === "mine") return t("You have no lessons yet");
    if (authorFilter.mode === "author") {
      return authorFilter.authorName
        ? fill(t("No lessons by {author} yet"), { author: authorFilter.authorName })
        : t("No lessons by that teacher yet");
    }
    return null;
  }, [isPage, authorFilter, lessons.length, filteredLessons.length, t]);

  // Hands the whole payload through, `source` included: the finder has to know
  // whether Saras chose these or a deterministic ranker did before it puts a
  // name to the reasons.
  const onAiMatch = useCallback(
    (text) => apiMatchLessonPlans(fetchWithAuth, { query: text, board }),
    [board, fetchWithAuth]
  );

  // Nothing seeded for this board — stay silent rather than showing an empty
  // shell on every dashboard.
  if (!loading && !error && lessons.length === 0) return null;

  return (
    <View
      testID="lesson-launcher"
      style={style}
      onLayout={(event) => setMeasuredWidth(event.nativeEvent.layout.width)}
    >
      {isPage ? (
        <View testID="lesson-hero" style={styles.hero}>
          <Image
            source={SARAS_MARK}
            style={isNarrow ? styles.heroMarkNarrow : styles.heroMark}
            resizeMode="contain"
            accessibilityIgnoresInvertColors
          />
          <Text
            style={[
              isNarrow ? styles.heroGreetingNarrow : styles.heroGreeting,
              { color: colors.textPrimary },
            ]}
          >
            {t("What are you teaching today?")}
          </Text>
        </View>
      ) : (
        <View style={styles.header}>
          <View style={[styles.headerIcon, { backgroundColor: colors.accentPrimary }]}>
            <MaterialCommunityIcons
              name="clipboard-text-outline"
              size={16}
              color="#ffffff"
            />
          </View>
          <View style={styles.headerText}>
            <Text style={[styles.headerTitle, { color: colors.textPrimary }]}>
              {t(title)}
            </Text>
            <Text style={[styles.headerSubtitle, { color: colors.textMuted }]}>
              {t(subtitle)}
            </Text>
          </View>
          {lessons.length > 0 || headerRight ? (
            <View
              testID="lesson-header-actions"
              style={[
                styles.headerActions,
                isNarrow ? styles.headerActionsStacked : styles.headerActionsInline,
              ]}
            >
              {lessons.length > 0 ? (
                <ProgressMeter
                  completed={completedCount}
                  total={lessons.length}
                  colors={colors}
                  isDarkMode={isDarkMode}
                  t={t}
                />
              ) : null}
              {headerRight}
            </View>
          ) : null}
        </View>
      )}

      {banner}

      {loading ? (
        <View style={styles.stateBox}>
          <ActivityIndicator size="small" color={colors.accentPrimary} />
          <Text style={[styles.stateText, { color: colors.textMuted }]}>
            {t("Loading lesson plans…")}
          </Text>
        </View>
      ) : error ? (
        <View
          style={[
            styles.stateBox,
            { borderColor: colors.border, borderWidth: 1, borderRadius: 12 },
          ]}
        >
          <MaterialCommunityIcons
            name="alert-outline"
            size={16}
            color={colors.textMuted}
          />
          <Text style={[styles.stateText, { color: colors.textMuted }]}>
            {t("Lesson plans could not be loaded.")}
          </Text>
        </View>
      ) : (
        <>
          {isPage ? (
            <LessonAuthorFilter
              lessons={lessons}
              onChange={setAuthorFilter}
              isDarkMode={isDarkMode}
              t={t}
            />
          ) : null}

          {/* Bridges onForkLesson/forkingLessonId to every row LessonFinder and
              LessonBrowseTree render, without either of those files knowing
              fork exists — see LessonResultRow.js. */}
          <LessonForkProvider
            value={{ onFork: onForkLesson, forking: forkingLessonId, audience }}
          >
            {filterEmptyText ? (
              <View
                testID="lesson-launcher-filter-empty"
                style={[styles.filterEmpty, { borderColor: colors.border }]}
              >
                <MaterialCommunityIcons
                  name="account-search-outline"
                  size={20}
                  color={colors.textMuted}
                />
                <Text style={[styles.filterEmptyText, { color: colors.textPrimary }]}>
                  {filterEmptyText}
                </Text>
              </View>
            ) : (
              <LessonFinder
                lessons={filteredLessons}
                progressByLesson={progressByLesson}
                isDarkMode={isDarkMode}
                onOpenLesson={onOpenLesson}
                onEditLesson={onEditLesson}
                onAiMatch={typeof fetchWithAuth === "function" ? onAiMatch : null}
                audience={audience}
                layout={layout}
                t={t}
              />
            )}
          </LessonForkProvider>

          {isPage && (onCreateLesson || onLessonImported) ? (
            <StartLessonCard
              onCreateLesson={onCreateLesson}
              fetchWithAuth={fetchWithAuth}
              onImported={onLessonImported}
              isDarkMode={isDarkMode}
              t={t}
            />
          ) : null}
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  // One question, centred, with the box under it. The mark sits above rather
  // than beside the line so the greeting stays optically centred on the page's
  // own axis — a mark on the left shifts the text off it.
  hero: { alignItems: "center", marginBottom: 22 },
  heroMark: { width: 40, height: 40, borderRadius: 12, marginBottom: 16 },
  heroMarkNarrow: { width: 32, height: 32, borderRadius: 10, marginBottom: 12 },
  // Large enough to be the first thing read and the only thing competing for
  // it. The negative tracking is what keeps a 30px line from reading as a
  // heading in a document.
  heroGreeting: {
    fontSize: 30,
    fontWeight: "700",
    lineHeight: 38,
    letterSpacing: -0.4,
    textAlign: "center",
  },
  heroGreetingNarrow: {
    fontSize: 23,
    fontWeight: "700",
    lineHeight: 30,
    letterSpacing: -0.3,
    textAlign: "center",
  },

  header: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    rowGap: 10,
    marginBottom: 16,
  },
  headerIcon: {
    width: 30,
    height: 30,
    borderRadius: 9,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 10,
  },
  headerText: { flex: 1, minWidth: 0 },
  headerTitle: { fontSize: 16, fontWeight: "800" },
  headerSubtitle: { fontSize: 12, marginTop: 1 },
  headerActions: { flexDirection: "row", alignItems: "center", gap: 12 },
  headerActionsInline: { marginLeft: "auto" },
  // Below the breakpoint the actions take their own line rather than squeezing
  // the title into two words.
  headerActionsStacked: { marginLeft: 0, width: "100%" },

  meter: { flexDirection: "row", alignItems: "center", gap: 10 },
  meterTrack: {
    width: METER_TRACK,
    height: 6,
    borderRadius: 999,
    overflow: "hidden",
  },
  meterFill: { height: "100%", borderRadius: 999 },
  meterText: { fontSize: 12, fontWeight: "700" },

  resume: {
    flexDirection: "row",
    alignItems: "center",
    gap: 14,
    borderRadius: 14,
    borderWidth: 1,
    borderLeftWidth: 3,
    paddingHorizontal: 16,
    paddingVertical: 14,
    marginBottom: 18,
  },
  resumeStacked: { flexDirection: "column", alignItems: "stretch" },
  resumeBody: { flex: 1, minWidth: 0 },
  resumeEyebrow: {
    fontSize: 10,
    fontWeight: "800",
    letterSpacing: 0.8,
    textTransform: "uppercase",
  },
  resumeTitle: { fontSize: 15, fontWeight: "700", lineHeight: 20, marginTop: 3 },
  resumeMeta: { fontSize: 12, fontWeight: "600", marginTop: 3 },

  stateBox: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 14,
    paddingHorizontal: 12,
    gap: 8,
  },
  stateText: { fontSize: 13, marginLeft: 8 },

  // Stands where the finder box would otherwise sit: a filter that narrowed a
  // real board to nothing still deserves the same weight of answer as "no
  // lessons match those words" does inside the finder itself.
  filterEmpty: {
    width: "100%",
    maxWidth: 680,
    alignSelf: "center",
    borderWidth: 1,
    borderRadius: 12,
    padding: 18,
    alignItems: "center",
    gap: 6,
  },
  filterEmptyText: { fontSize: 14, fontWeight: "700", marginTop: 4, textAlign: "center" },
});

export default LessonPlanLauncher;
